/* ════════════ BURN AFTER READING ════════════
   The CIA page's atmosphere: dossiers drift through the dark until
   one ignites from a corner and crumbles into ░▒ ash. The records
   were destroyed in 1973. The habit wasn't. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function burnfile(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#burnfile")!;
  const ctx = c.getContext("2d")!;

  const VIOLET = "#8a7dff";
  const GREY = "#9aa0a6";
  const GLYPHS = ["░", "▒"];

  type Doc = {
    x: number;
    y: number;
    w: number;
    h: number;
    vy: number; // slow drift
    t: number; // age in frames
    life: number;
    burnAt: number; // fraction of life when ignition starts
    lines: number; // redacted text lines
    col: string;
  };
  type Ash = {
    x: number;
    y: number;
    vx: number;
    vy: number;
    t: number;
    life: number;
    g: string;
    col: string;
  };
  let docs: Doc[] = [];
  let ashes: Ash[] = [];

  const spawn = (): Doc => {
    const isHot = Math.random() < 0.18;
    const w = 24 + Math.random() * 22;
    return {
      x: Math.random() * c.width,
      y: Math.random() * c.height,
      w,
      h: w * 1.3,
      vy: -(0.04 + Math.random() * 0.1),
      t: 0,
      life: 540 + Math.random() * 480,
      burnAt: 0.55 + Math.random() * 0.2,
      lines: 3 + Math.floor(Math.random() * 3),
      col: isHot ? VIOLET : GREY,
    };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    const count = Math.min(14, Math.floor((c.width * c.height) / 110000));
    docs = Array.from({ length: count }, () => {
      const d = spawn();
      d.t = Math.random() * d.life * 0.6; // desync, mostly pre-burn
      return d;
    });
    ashes = [];
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.lineWidth = 1;

    for (const d of docs) {
      const p = d.t / d.life;
      const hot = d.col === VIOLET;
      const base = hot ? 0.45 : 0.18;
      // fade in over the first 12%, then burn handles the exit
      const fade = p < 0.12 ? p / 0.12 : 1;
      // burn progress: the page is consumed from the bottom corner up
      const q = p < d.burnAt ? 0 : (p - d.burnAt) / (1 - d.burnAt);
      const hLeft = d.h * (1 - q); // unburned height, measured from the top
      if (hLeft < 1) continue;

      ctx.globalAlpha = base * fade;
      ctx.strokeStyle = d.col;
      ctx.strokeRect(d.x, d.y, d.w, hLeft);

      // redacted text lines, lost one by one to the fire
      for (let i = 0; i < d.lines; i++) {
        const ly = d.y + 5 + i * (d.h / (d.lines + 1));
        if (ly > d.y + hLeft - 3) break;
        ctx.beginPath();
        ctx.moveTo(d.x + 4, ly);
        ctx.lineTo(d.x + d.w - 4 - (i % 2) * d.w * 0.25, ly);
        ctx.stroke();
      }

      // the burn edge glows hotter than the page
      if (q > 0) {
        ctx.globalAlpha = Math.min(1, base * 2.2);
        ctx.strokeStyle = VIOLET;
        ctx.beginPath();
        ctx.moveTo(d.x, d.y + hLeft);
        ctx.lineTo(d.x + d.w, d.y + hLeft);
        ctx.stroke();
      }
    }

    ctx.font = "9px 'IBM Plex Mono', monospace";
    for (const a of ashes) {
      const p = a.t / a.life;
      ctx.globalAlpha = (1 - p) * 0.3;
      ctx.fillStyle = a.col;
      ctx.fillText(a.g, a.x, a.y);
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (let i = 0; i < docs.length; i++) {
      const d = docs[i]!;
      d.t++;
      d.y += d.vy;
      const p = d.t / d.life;
      // burning pages shed ash glyphs from the burn edge
      if (p > d.burnAt && ashes.length < 70 && Math.random() < 0.25) {
        const q = (p - d.burnAt) / (1 - d.burnAt);
        ashes.push({
          x: d.x + Math.random() * d.w,
          y: d.y + d.h * (1 - q),
          vx: (Math.random() - 0.5) * 0.4,
          vy: 0.2 + Math.random() * 0.5,
          t: 0,
          life: 90 + Math.random() * 90,
          g: GLYPHS[Math.floor(Math.random() * GLYPHS.length)]!,
          col: d.col,
        });
      }
      if (d.t >= d.life || d.y + d.h < 0) docs[i] = spawn();
    }
    for (let i = ashes.length - 1; i >= 0; i--) {
      const a = ashes[i]!;
      a.t++;
      a.x += a.vx;
      a.y += a.vy;
      if (a.t >= a.life) ashes.splice(i, 1);
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage(burnfile);
